import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import dayjs from 'dayjs';
import { Link as RouterLink } from 'react-router-dom';
import { retrieveTask } from '@src/api/tasks';
import TaskExpandedContent, { SOURCE_BADGE, deepLinkForTask } from './TaskExpandedContent';
import { SnoozeMenu } from './TaskTable';

const PRIORITY_DOT = {
  critical: 'error.main',
  high: 'warning.main',
  medium: 'info.main',
  low: 'grey.400',
};

// Age of the task since creation — "5h" under a day, "2d" after. Amber at
// 2+ days, red at 4+ so stale items jump out while scanning the queue.
function agingChip(task) {
  if (!task.created_at) return null;
  const created = dayjs(task.created_at);
  if (!created.isValid()) return null;
  // Age is always visible — it's the primary triage signal. Minutes under an
  // hour, hours under a day, then days; amber at 2d, red at 4d.
  const minutes = Math.max(dayjs().diff(created, 'minute'), 0);
  const hours = Math.floor(minutes / 60);
  const days = dayjs().diff(created, 'day');
  let label;
  if (days >= 1) label = `${days}d`;
  else if (hours >= 1) label = `${hours}h`;
  else label = `${Math.max(minutes, 1)}m`;
  const color = days >= 4 ? 'error' : days >= 2 ? 'warning' : 'default';
  return { label, color, title: `Created ${created.format('MMM D, YYYY h:mm A')}` };
}

// True when the keydown originated in a typing context (text field, select,
// or the TipTap contenteditable editor) — those keys belong to the editor.
function isTypingTarget(target) {
  const tag = (target?.tagName || '').toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select' || Boolean(target?.isContentEditable);
}

/**
 * Keyboard-driven split-pane triage view for the /tasks page.
 * Left: compact scrollable task list. Right: the same detail content the
 * table's expanded row shows (TaskExpandedContent), plus a slim action header.
 * j/k move · s snooze until tomorrow 9am · c toggle complete · esc exit.
 */
function TaskFocusMode({
  tasks,
  hasMore,
  loadingMore,
  onLoadMore,
  onExit,
  onApproveProposal,
  onRejectProposal,
  onRefreshTask,
  onDomainApproved,
  onSnooze,
  onToggleComplete,
}) {
  const [selectedId, setSelectedId] = useState(() => {
    const firstOpen = tasks.find((t) => !t.is_completed) || tasks[0];
    return firstOpen ? firstOpen.id : null;
  });
  const listRef = useRef(null);
  const sentinelRef = useRef(null);
  const rowRefs = useRef({});
  // Remembers where the selection last sat so that when the selected task is
  // removed from the array (approved/completed with hide-completed on) we can
  // advance from that position instead of snapping back to the top.
  const lastIndexRef = useRef(0);

  const selectedIndex = useMemo(
    () => tasks.findIndex((t) => t.id === selectedId),
    [tasks, selectedId],
  );
  const selectedTask = selectedIndex >= 0 ? tasks[selectedIndex] : null;

  useEffect(() => {
    if (selectedIndex >= 0) lastIndexRef.current = selectedIndex;
  }, [selectedIndex]);

  // AUTO-ADVANCE: when the selected task completes (prop change after a
  // send/approve/complete) or vanishes from the list, move the selection to
  // the next open task — forward first, then backward as a fallback.
  useEffect(() => {
    if (!tasks.length) {
      if (selectedId !== null) setSelectedId(null);
      return;
    }
    const idx = tasks.findIndex((t) => t.id === selectedId);
    if (idx >= 0 && !tasks[idx].is_completed) return;
    const from = idx >= 0 ? idx + 1 : Math.min(lastIndexRef.current, tasks.length - 1);
    let next = null;
    for (let i = from; i < tasks.length; i += 1) {
      if (!tasks[i].is_completed) { next = tasks[i]; break; }
    }
    if (!next) {
      for (let i = Math.min(from, tasks.length) - 1; i >= 0; i -= 1) {
        if (!tasks[i].is_completed) { next = tasks[i]; break; }
      }
    }
    // Everything is completed (hide-completed off): keep a valid selection.
    if (!next && idx < 0) next = tasks[Math.min(lastIndexRef.current, tasks.length - 1)];
    if (next && next.id !== selectedId) setSelectedId(next.id);
  }, [tasks, selectedId]);

  const moveSelection = useCallback(
    (delta) => {
      if (!tasks.length) return;
      const current = tasks.findIndex((t) => t.id === selectedId);
      const nextIndex = current < 0 ? 0 : current + delta;
      if (nextIndex < 0) return;
      if (nextIndex >= tasks.length) {
        // Selecting past the end pulls in the next page.
        if (hasMore) onLoadMore();
        return;
      }
      setSelectedId(tasks[nextIndex].id);
    },
    [tasks, selectedId, hasMore, onLoadMore],
  );

  // Window-level shortcuts while focus mode is mounted. Typing contexts
  // (inputs, textareas, selects, the TipTap contenteditable) and chorded
  // keys (meta/ctrl/alt) are left alone so editing keeps working normally.
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (isTypingTarget(event.target)) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key === 'j' || event.key === 'ArrowDown') {
        event.preventDefault();
        moveSelection(1);
      } else if (event.key === 'k' || event.key === 'ArrowUp') {
        event.preventDefault();
        moveSelection(-1);
      } else if (event.key === 's') {
        if (!selectedTask) return;
        event.preventDefault();
        // Mirrors SnoozeMenu's "Tomorrow 9am" option.
        const until = dayjs().add(1, 'day').hour(9).minute(0).second(0).toISOString();
        onSnooze(selectedTask, until);
      } else if (event.key === 'c') {
        if (!selectedTask) return;
        event.preventDefault();
        onToggleComplete(selectedTask, !selectedTask.is_completed);
      } else if (event.key === 'Escape') {
        event.preventDefault();
        onExit();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [moveSelection, selectedTask, onSnooze, onToggleComplete, onExit]);

  // Keep the selected row visible as j/k walk the list.
  useEffect(() => {
    const el = rowRefs.current[selectedId];
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [selectedId]);

  // Auto-load the next page when the left pane scrolls near its bottom —
  // same sentinel pattern as the table view, rooted on the pane scroller.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) onLoadMore();
      },
      { root: listRef.current, rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [onLoadMore]);

  // After a reply is sent from the inline email panel the backend closes the
  // task — re-fetch it so the fresh is_completed state flows back into the
  // page's task array and auto-advance moves on. (Mirrors TaskTable.)
  const handleEmailTaskUpdated = (task) => {
    if (!onRefreshTask) return;
    retrieveTask(task.id)
      .then((response) => onRefreshTask(response.data))
      .catch(() => onRefreshTask({ ...task, is_completed: true }));
  };

  const deepLink = selectedTask ? deepLinkForTask(selectedTask) : null;

  return (
    <Paper
      variant="outlined"
      sx={{
        display: 'flex',
        height: 'calc(100vh - 240px)',
        minHeight: 480,
        overflow: 'hidden',
      }}
    >
      {/* Left pane — compact triage list */}
      <Box
        sx={{
          width: 340,
          flexShrink: 0,
          borderRight: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}
        >
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.5, flexGrow: 1 }}>
            Focus mode · {tasks.length} task{tasks.length === 1 ? '' : 's'}
          </Typography>
          <Tooltip title="Exit focus mode (esc)">
            <IconButton size="small" onClick={onExit}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
        <Box ref={listRef} sx={{ flexGrow: 1, overflowY: 'auto', minHeight: 0 }}>
          {tasks.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
              No tasks in the current view.
            </Typography>
          ) : (
            tasks.map((task) => {
              const isSelected = task.id === selectedId;
              const sourceBadge = task.source_domain ? SOURCE_BADGE[task.source_domain] : null;
              const aging = agingChip(task);
              return (
                <Box
                  key={task.id}
                  ref={(el) => { rowRefs.current[task.id] = el; }}
                  onClick={() => setSelectedId(task.id)}
                  sx={{
                    px: 1.5,
                    py: 1,
                    cursor: 'pointer',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    borderLeft: '3px solid',
                    borderLeftColor: isSelected ? 'primary.main' : 'transparent',
                    backgroundColor: isSelected ? 'action.selected' : 'transparent',
                    opacity: task.is_completed ? 0.5 : 1,
                    '&:hover': { backgroundColor: isSelected ? 'action.selected' : 'action.hover' },
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: PRIORITY_DOT[task.priority] || 'grey.400',
                        flexShrink: 0,
                        mt: 0.75,
                      }}
                    />
                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        {task.is_completed ? (
                          <CheckCircleIcon sx={{ fontSize: 14, color: 'success.main', flexShrink: 0 }} />
                        ) : null}
                        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                          {task.title}
                        </Typography>
                      </Stack>
                      {task.client_name ? (
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                          {task.client_name}
                        </Typography>
                      ) : (
                        <Chip size="small" label="Internal" variant="outlined" sx={{ height: 18, fontSize: '0.65rem', mt: 0.25 }} />
                      )}
                    </Box>
                    <Stack spacing={0.5} alignItems="flex-end" sx={{ flexShrink: 0 }}>
                      {sourceBadge ? (
                        <Chip size="small" label={sourceBadge.label} color={sourceBadge.color} variant="outlined" />
                      ) : null}
                      {aging ? (
                        <Chip
                          size="small"
                          label={aging.label}
                          color={aging.color}
                          variant="outlined"
                          title={aging.title}
                        />
                      ) : null}
                    </Stack>
                  </Stack>
                </Box>
              );
            })
          )}
          <Box ref={sentinelRef} sx={{ height: 1 }} />
          {loadingMore ? (
            <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ py: 1 }}>
              <CircularProgress size={14} />
              <Typography variant="caption" color="text.secondary">
                Loading more…
              </Typography>
            </Stack>
          ) : null}
        </Box>
        <Box sx={{ px: 1.5, py: 0.75, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">
            j/k move · s snooze · c complete · n new task · esc exit
          </Typography>
        </Box>
      </Box>

      {/* Right pane — slim action header + the expanded-row detail content */}
      {selectedTask ? (
        <Box
          key={selectedTask.id}
          sx={{ flex: 1, overflowY: 'auto', minWidth: 0, minHeight: 0 }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              px: 2,
              py: 1.25,
              borderBottom: '1px solid',
              borderColor: 'divider',
              position: 'sticky',
              top: 0,
              zIndex: 1,
              backgroundColor: 'background.paper',
            }}
          >
            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>
                {selectedTask.title}
              </Typography>
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                {!selectedTask.client_name ? (
                  <Chip size="small" label="Internal" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                ) : null}
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                  {[
                    selectedTask.client_name,
                    selectedTask.due_date ? `Due ${dayjs(selectedTask.due_date).format('MMM D, YYYY')}` : null,
                  ].filter(Boolean).join(' · ') || (selectedTask.client_name ? '—' : '')}
                </Typography>
              </Stack>
            </Box>
            <Tooltip title={selectedTask.is_completed ? 'Reopen (c)' : 'Mark done (c)'}>
              <IconButton
                size="small"
                onClick={() => onToggleComplete(selectedTask, !selectedTask.is_completed)}
                sx={{ color: selectedTask.is_completed ? 'success.main' : 'text.secondary' }}
              >
                {selectedTask.is_completed ? (
                  <CheckCircleIcon fontSize="small" />
                ) : (
                  <CheckCircleOutlineIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
            <SnoozeMenu task={selectedTask} onSnooze={onSnooze} />
            {deepLink ? (
              <Tooltip title={deepLink.label}>
                <IconButton
                  size="small"
                  component={RouterLink}
                  to={deepLink.to}
                  sx={{ color: 'primary.main' }}
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
          </Stack>
          <TaskExpandedContent
            task={selectedTask}
            onApproveProposal={onApproveProposal}
            onRejectProposal={onRejectProposal}
            onRefreshTask={onRefreshTask}
            onDomainApproved={onDomainApproved}
            onEmailTaskUpdated={handleEmailTaskUpdated}
          />
        </Box>
      ) : (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Select a task on the left to work it here.
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

TaskFocusMode.propTypes = {
  tasks: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      title: PropTypes.string,
      client_name: PropTypes.string,
      source_domain: PropTypes.string,
      priority: PropTypes.string,
      created_at: PropTypes.string,
      is_completed: PropTypes.bool,
    }),
  ),
  hasMore: PropTypes.bool,
  loadingMore: PropTypes.bool,
  onLoadMore: PropTypes.func.isRequired,
  onExit: PropTypes.func.isRequired,
  onApproveProposal: PropTypes.func.isRequired,
  onRejectProposal: PropTypes.func.isRequired,
  onRefreshTask: PropTypes.func,
  onDomainApproved: PropTypes.func,
  onSnooze: PropTypes.func.isRequired,
  onToggleComplete: PropTypes.func.isRequired,
};

TaskFocusMode.defaultProps = {
  tasks: [],
  hasMore: false,
  loadingMore: false,
  onRefreshTask: null,
  onDomainApproved: null,
};

export default TaskFocusMode;
